<script setup lang="ts">
import { useFormValidation } from "../../../../../composables/useFormValidation";
import { required } from "../../../../../utils/validation";
import TextInput from "../../../../../components/ui/form/TextInput.vue";
import NumberInput from "../../../../../components/ui/form/NumberInput.vue";
import CheckInput from "../../../../../components/ui/form/CheckInput.vue";
import { api } from "../../../../../api/Api";
import Toast from "../../../../../services/Toast";
import { Permission } from "../../../../../types/auth";
import { ref, onMounted } from "vue";
import ModalFormTemplate from "../../../../../components/ui/Templates/ModalFormTemplate.vue";
import SearchSelect from "../../../../../components/ui/form/SearchSelect.vue";

const props = defineProps<{
  data?: any | null;
  mode: "create" | "edit" | "view" | "showQuickView" | "toggleStatus";
  title: string | null;
  endPoint: string;
  permissions?: Permission[];
  listCreateName?: string;
  unit_of_measures?: any | [];
  item?: any | null;
}>();

const emit = defineEmits<{
  (e: "save", data: any): void;
  (e: "close"): void;
}>();

const {
  fields,
  formData,
  setFieldRules,
  validateForm,
  resetForm,
  setFormData,
} = useFormValidation({
  items_id: props.data?.items_id || "",
  item: props.data?.item || "",
  warehouse: props.data?.warehouse || "",
  warehouses_id: props.data?.warehouses_id || "",
  reorder_uom_id: props.data?.warehouses_id || "",
  on_hand: props.data?.warehouses_id || 0,
  cost: props.data?.warehouses_id || 0,
  reorder_level: props.data?.warehouses_id || 0,
  reorder_quantity: props.data?.warehouses_id || 0,
  minimum: props.data?.warehouses_id || 0,
  maximum: props.data?.warehouses_id || 0,
  active: props.data?.active || true,
  uid: props.data?.uid || "",
});

setFieldRules("items_id", [required("Item is required")]);
setFieldRules("warehouses_id", [required("Warehouse is required")]);

const handleSubmit = async (close: boolean, newRecord: boolean) => {
  if (validateForm()) {
    const action = formData.value.uid != "" ? "update" : "create";
    const response = await api.post(
      (props.endPoint.slice(-1) == "/" || props.endPoint.slice(-1) == "-"
        ? props.endPoint
        : props.endPoint + "/") + action,
      formData.value
    );
    if (response.success == true) {
      Toast.show("saved successfully");
      emit("save", response.data);
      if (close) {
        emit("close");
      } else if (newRecord) {
        resetForm();
      } else {
        setFormData(response.data);
      }
    }
  }
};

const isReadOnly = ref(true);
onMounted(() => {
  isReadOnly.value = props.mode == 'view'
  if (props.mode == 'create') {
      resetForm()
    } else if (props.data) {
      setFormData(props.data)
    }
})


</script>
<template>
  <ModalFormTemplate
    :data="data"
    :mode="mode"
    :title="title"
    :endPoint="endPoint"
    :perm="permissions"
    :listCreateName="listCreateName"
    :tabList="[]"
    :formData="formData"
    @save="handleSubmit"
    @close="$emit('close')"
  >
    <template #form>
      <div class="grid md:grid-cols-2 gap-6">
        <TextInput
          v-model="fields.item.value"
          label="Item"
          :readonly="true"
          :error="fields.item.touched ? fields.item.error : null"
          @blur="fields.item.touched = true"
        />

        <TextInput
          v-model="fields.warehouse.value"
          label="Warehouse"
          :readonly="true"
          :error="fields.warehouse.touched ? fields.warehouse.error : null"
          @blur="fields.warehouse.touched = true"
        />
      </div>
      <div class="grid md:grid-cols-3 gap-6">
        <!--<NumberInput v-model="fields.inventory_qty.value" :readonly="isReadOnly" label="Inventory Quantity" :min="1"
          :error="fields.inventory_qty.touched ? fields.inventory_qty.error : null"
          @blur="fields.inventory_qty.touched = true" />-->

        <NumberInput
          v-model="fields.on_hand.value"
          :readonly="true"
          label="On Hand"
          :min="0"
          :error="fields.on_hand.touched ? fields.on_hand.error : null"
          @blur="fields.on_hand.touched = true"
        />

        <NumberInput
          v-model="fields.cost.value"
          :readonly="true"
          label="Cost"
          :min="0"
          :error="fields.cost.touched ? fields.cost.error : null"
          @blur="fields.cost.touched = true"
        />

        <NumberInput
          v-model="fields.reorder_level.value"
          :readonly="isReadOnly"
          label="Reorder Level"
          :min="0"
          :error="
            fields.reorder_level.touched ? fields.reorder_level.error : null
          "
          @blur="fields.reorder_level.touched = true"
        />

        <SearchSelect
          v-model="fields.reorder_uom_id.value"
          end-point="inventory/item-management/unit-of-measures"
          label="Reorder Unit of Measure"
          :readonly="isReadOnly"
          :remote="true"
          :options="unit_of_measures"
          :error="
            fields.reorder_uom_id.touched ? fields.reorder_uom_id.error : null
          "
          @blur="fields.reorder_uom_id.touched = true"
        />

        <NumberInput
          v-model="fields.reorder_quantity.value"
          :readonly="isReadOnly"
          label="Reorder Quantity"
          :min="0"
          :error="
            fields.reorder_quantity.touched
              ? fields.reorder_quantity.error
              : null
          "
          @blur="fields.reorder_quantity.touched = true"
        />

        <NumberInput
          v-model="fields.minimum.value"
          :readonly="isReadOnly"
          label="Minimum"
          :min="0"
          :error="fields.minimum.touched ? fields.minimum.error : null"
          @blur="fields.minimum.touched = true"
        />

        <NumberInput
          v-model="fields.maximum.value"
          :readonly="isReadOnly"
          label="Maximum"
          :min="0"
          :error="fields.maximum.touched ? fields.maximum.error : null"
          @blur="fields.maximum.touched = true"
        />

        <CheckInput
          v-model="fields.active.value"
          :readonly="true"
          label="Active"
          :error="fields.active.touched ? fields.active.error : null"
          @blur="fields.active.touched = true"
        />
      </div>
    </template>
  </ModalFormTemplate>
</template>
